import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Prediction } from '../../types/predictions';
import { formatPrediction } from '../../utils/priceUtils';
import { formatDate } from '../../utils/dateUtils';
import InfluencerScoreDots from './InfluencerScoreDots';
import SharePrediction from '../SharePrediction';
import AdSenseSlot from '../AdSenseSlot';

const PredictionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scrolling */
  min-height: 0;
  width: 100%;
  max-width: 100%; /* Ensure list never exceeds container width */
  box-sizing: border-box;
  
  /* Add bottom padding to prevent scroll trap */
  padding-bottom: 120px; /* Increased from 100px for more space */
  
  /* Improved scroll behavior for mobile */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  overscroll-behavior-x: none; /* Prevent horizontal overscroll */
  
  /* Ensure there's always scrollable content */
  &::after {
    content: '';
    height: 1px;
    width: 100%;
    flex-shrink: 0;
  }
  
  /* Ensure all prediction cards respect container bounds */
  & > * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const PredictionCard = styled.div<{ status?: string; $active?: boolean }>`
  background: ${props => props.$active ? props.theme.hoverBackground : props.theme.surface};
  border-radius: 16px;
  padding: 20px 16px; /* Tighter padding - content closer to borders */
  margin-bottom: 20px; 
  min-height: 160px; /* Good height without being excessive */
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12); /* Enhanced shadow */
  border-left: 4px solid ${({ status = 'pending' }) => {
    switch (status) {
      case 'success': return '#4CAF50';
      case 'miss': return '#F44336';
      case 'updated': return '#9C27B0';
      case 'ad': return '#F7931A';
      default: return '#FF9800';
    }
  }};
  ${({ $active, theme }) => $active && `
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(247, 147, 26, 0.3);
    border: 2px solid ${theme.primary || '#F7931A'};
    border-left: 4px solid ${theme.primary || '#F7931A'};
  `}
  transition: all 0.2s ease;
  cursor: pointer;
  
  /* FULL CARD CLICKABLE - Ensure entire card responds to touch */
  position: relative;
  z-index: 1;
  
  /* Touch-friendly settings */
  -webkit-tap-highlight-color: rgba(247, 147, 26, 0.1);
  touch-action: manipulation;
  
  /* Prevent all internal scrolling */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden; /* No scrolling inside cards */
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Active/pressed state for mobile */
  &:active {
    transform: scale(0.98);
    transition: all 0.1s ease;
  }
`;

const PredictionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px; /* Good spacing without being excessive */
`;

const ExpertName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.theme.text};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  background-color: ${({ status }) => {
    switch (status) {
      case 'success': return '#4CAF50';
      case 'miss': return '#F44336';
      case 'updated': return '#9C27B0';
      case 'ad': return '#F7931A';
      default: return '#FF9800';
    }
  }};
`;

const AdPlaceholderBox = styled.div`
  height: 168px;
  min-height: 168px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  overflow: hidden;
  width: 100%;
  flex: 0 0 168px;
`;

const StyledMobileAdSenseSlot = styled(AdSenseSlot)`
  width: 100%;
  height: 168px;
  min-height: 168px;
`;

const BTC_MOBILE_AD_SLOT = '2902895851';
const BTC_MOBILE_AD_LAYOUT_KEY = '-ef+6k-30-ac+ty';

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 12px 0; /* Balanced spacing */
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  color: ${props => props.theme.subText};
  font-weight: 500;
`;

const PredictionDetails = styled.div`
  margin-bottom: 12px; /* Good spacing for content visibility */
`;

const PredictionText = styled.div`
  color: ${props => props.theme.text};
  font-size: 15px;
  margin-bottom: 10px; /* Compact but readable spacing */
  line-height: 1.4; /* Good readability without excess height */
  font-weight: 500;
`;

const TargetTime = styled.div`
  color: ${props => props.theme.subText};
  font-size: 13px;
  margin-bottom: 10px; /* Good spacing without excess */
`;

const QuoteSection = styled.div<{ $expanded: boolean }>`
  margin-top: 12px; /* Good separation without excess */
  padding: 14px; /* Balanced padding */
  background: ${props => props.theme.hoverBackground || 'rgba(128, 128, 128, 0.1)'};
  border-left: 3px solid ${props => props.theme.primary || '#F7931A'};
  border-radius: 8px;
  transition: all 0.3s ease;
  
  /* Enhanced shadow for depth */
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  
  /* Proper text wrapping without internal scrolling */
  overflow: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  &:hover {
    background: ${props => props.theme.activeBackground || 'rgba(128, 128, 128, 0.15)'};
    border-left-color: ${props => props.theme.primary || '#F7931A'};
  }
`;

const QuoteText = styled.div<{ $expanded: boolean }>`
  color: ${props => props.theme.text};
  font-size: 14px;
  line-height: 1.4;
  font-style: italic;
  /* Show full content with proper wrapping */
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden; /* Prevent any internal scrolling */
  
  &::before {
    content: '"';
    opacity: 0.6;
    color: ${props => props.theme.primary};
    font-weight: bold;
    margin-right: 2px;
  }
  
  &::after {
    content: '"';
    opacity: 0.6;
    color: ${props => props.theme.primary};
    font-weight: bold;
    margin-left: 2px;
  }
`;

const QuoteToggle = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 11px;
  margin-top: 6px;
  padding: 4px 8px;
  cursor: pointer;
  text-decoration: none;
  border-radius: 12px;
  background: ${props => props.theme.hoverBackground};
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.theme.activeBackground};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const QuoteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  color: ${props => props.theme.subText};
  opacity: 0.9;
  font-weight: 500;
  
  span:first-child {
    font-size: 14px; /* Appropriately sized emoji */
  }
`;

const PredictionTypeIcon = styled.span`
  margin-right: 4px;
  font-size: 12px;
`;

const MobileSubmitCard = styled.div`
  padding: 16px;
  background: ${props => props.theme.hoverBackground};
  border: 2px dashed ${props => props.theme.border};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  margin-bottom: 8px;
  flex-shrink: 0;
  
  &:active {
    transform: scale(0.98);
    background: ${props => props.theme.activeBackground};
  }
`;

const SubmitCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SubmitCardIcon = styled.div`
  font-size: 28px;
  margin-bottom: 4px;
`;

const SubmitCardTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 2px;
`;

const SubmitCardSubtitle = styled.div`
  font-size: 12px;
  color: ${props => props.theme.subText};
  opacity: 0.9;
`;

// User Contest Prediction Card - Matches PredictionCard style with special golden animation
const UserContestCard = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #F7931A;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  min-height: fit-content;
  
  /* Pulsing glow animation */
  animation: contestPulse 2s ease-in-out infinite;
  
  @keyframes contestPulse {
    0%, 100% {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(247, 147, 26, 0.4);
    }
    50% {
      box-shadow: 0 4px 12px rgba(247, 147, 26, 0.3), 0 0 20px 4px rgba(247, 147, 26, 0.2);
    }
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const UserContestCardContent = styled.div`
  position: relative;
  z-index: 1;
`;

const UserContestBadge = styled.span`
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  color: #000;
  background: linear-gradient(135deg, #F7931A 0%, #FFB347 100%);
`;



// Countdown Timer Styled Components
const CountdownContainer = styled.div`
  background: linear-gradient(135deg, rgba(247, 147, 26, 0.15) 0%, rgba(247, 147, 26, 0.05) 100%);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CountdownLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CountdownInfluencer = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CountdownTimer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TimeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 45px;
`;

const TimeValue = styled.div`
  font-size: 22px;
  font-weight: bold;
  color: ${props => props.theme.primary || '#F7931A'};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const TimeLabel = styled.div`
  font-size: 9px;
  color: ${props => props.theme.subText};
  text-transform: uppercase;
  margin-top: 2px;
`;

const TimeSeparator = styled.div`
  font-size: 18px;
  color: ${props => props.theme.subText};
  opacity: 0.5;
  font-weight: bold;
`;

const PredictionTarget = styled.div`
  font-size: 12px;
  color: ${props => props.theme.text};
  opacity: 0.9;
`;

interface MobilePredictionsListProps {
  predictions: Prediction[];
  activePredictions: number[];
  onPredictionClick: (id: number) => void;
  openModal?: () => void;
  currentAsset?: 'btc' | 'mstr' | 'naka';
  // ARCHIVED: Contest props removed (April 2026 contest ended)
  // userContestPrediction?: number | null;
  // userContestEntryDate?: string;
  userHandle?: string;
}

const MobilePredictionsList: React.FC<MobilePredictionsListProps> = ({
  predictions,
  activePredictions,
  onPredictionClick,
  openModal,
  currentAsset = 'btc',
  // ARCHIVED: Contest params removed (April 2026 contest ended)
  // userContestPrediction,
  // userContestEntryDate,
  userHandle
}) => {

  const listRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [countdown, setCountdown] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  const visiblePredictionIds = React.useMemo(() => {
    return activePredictions.length > 0
      ? activePredictions
      : predictions.map(prediction => prediction.id);
  }, [activePredictions, predictions]);
  const shouldShowMobileAdPlaceholder = currentAsset === 'btc' && visiblePredictionIds.length >= 2;
  
  // ⚡ PERFORMANCE: Create prediction lookup map for O(1) access
  const predictionsMap = React.useMemo(() => {
    const map = new Map<number, Prediction>();
    predictions.forEach(p => map.set(p.id, p));
    return map;
  }, [predictions]);
  
  // ⚡ PERFORMANCE: Pre-compute influencer histories to avoid expensive calculations per card
  const influencerHistories = React.useMemo(() => {
    const histories = new Map<string, Prediction[]>();
    
    // Group predictions by influencer
    const influencerGroups = new Map<string, Prediction[]>();
    predictions.forEach(prediction => {
      const name = prediction.influencer?.name?.trim();
      if (name) {
        if (!influencerGroups.has(name)) {
          influencerGroups.set(name, []);
        }
        influencerGroups.get(name)!.push(prediction);
      }
    });
    
    // Sort and slice each influencer's history
    influencerGroups.forEach((preds, name) => {
      const sortedHistory = preds
        .sort((a, b) => {
          const aDate = new Date(a.prediction_made || a.created_at || a.end_date || 0);
          const bDate = new Date(b.prediction_made || b.created_at || b.end_date || 0);
          return aDate.getTime() - bDate.getTime();
        })
        .slice(-10); // Keep only the most recent 10
      
      histories.set(name, sortedHistory);
    });
    
    return histories;
  }, [predictions]);

  // Find the next upcoming prediction (pending with soonest end_date)
  const nextPrediction = React.useMemo(() => {
    const now = new Date();
    const pendingPredictions = predictions.filter(p => {
      const isPending = !p.success_status || p.success_status === 'pending';
      const targetDate = p.end_date ? new Date(p.end_date) : (p.target_time ? new Date(p.target_time) : null);
      // Exclude Easter Bunny prediction (ID 331) from countdown
      const isNotEasterBunny = p.id !== 331;
      // Only include if pending AND has a future date AND not Easter Bunny
      return isPending && targetDate && targetDate > now && isNotEasterBunny;
    });
    
    if (pendingPredictions.length === 0) return null;
    
    // Sort by nearest date
    pendingPredictions.sort((a, b) => {
      const aDate = new Date(a.end_date || a.target_time || 0);
      const bDate = new Date(b.end_date || b.target_time || 0);
      return aDate.getTime() - bDate.getTime();
    });
    
    return pendingPredictions[0];
  }, [predictions]);

  // Countdown timer effect
  useEffect(() => {
    if (!nextPrediction) {
      setCountdown(null);
      return;
    }

    const targetDate = new Date(nextPrediction.end_date || nextPrediction.target_time || 0);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [nextPrediction]);

  // Note: Removed auto-scroll to prevent jarring UX where chart disappears
  // User clicks should keep them at their current scroll position
  // They can manually scroll to the card if needed
  // Asset-specific information for submit card
  const assetInfo = React.useMemo(() => {
    switch (currentAsset) {
      case 'btc': return { name: 'Bitcoin', symbol: 'BTC', icon: '₿' };
      case 'mstr': return { name: 'MicroStrategy', symbol: 'MSTR', icon: '📈' };
      case 'naka': return { name: 'Nakamoto Games', symbol: 'NAKA', icon: '🎮' };
      default: return { name: 'Crypto', symbol: 'CRYPTO', icon: '💰' };
    }
  }, [currentAsset]);

  // Format price target for countdown display
  const getCountdownPriceDisplay = (pred: Prediction) => {
    if (pred.price_target) return `$${Number(pred.price_target).toLocaleString()}`;
    if (pred.price_min && pred.price_max) return `$${Number(pred.price_min).toLocaleString()} - $${Number(pred.price_max).toLocaleString()}`;
    if (pred.prediction_type === 'top') return 'Market Top';
    if (pred.prediction_type === 'bottom') return 'Market Bottom';
    return 'Target';
  };

  // Debug mobile countdown
  console.log('📱 Mobile Countdown debug:', { 
    predictionsCount: predictions.length,
    nextPrediction: nextPrediction ? { id: nextPrediction.id, name: nextPrediction.influencer?.name, end_date: nextPrediction.end_date } : null,
    countdown,
    firstPrediction: predictions[0] ? { id: predictions[0].id, name: predictions[0].influencer?.name, end_date: predictions[0].end_date, status: predictions[0].success_status } : null
  });

  return (
    <PredictionsList ref={listRef}>
      {/* Countdown Timer for Next Prediction */}
      {nextPrediction && countdown && (
        <CountdownContainer onClick={() => onPredictionClick(nextPrediction.id)}>
          <CountdownLabel>
            ⏰ Next prediction due
          </CountdownLabel>
          <CountdownInfluencer>
            {nextPrediction.influencer?.name || 'Unknown'}: {getCountdownPriceDisplay(nextPrediction)}
          </CountdownInfluencer>
          <CountdownTimer>
            {countdown.days > 0 && (
              <>
                <TimeUnit>
                  <TimeValue>{countdown.days}</TimeValue>
                  <TimeLabel>days</TimeLabel>
                </TimeUnit>
                <TimeSeparator>:</TimeSeparator>
              </>
            )}
            <TimeUnit>
              <TimeValue>{countdown.hours.toString().padStart(2, '0')}</TimeValue>
              <TimeLabel>hrs</TimeLabel>
            </TimeUnit>
            <TimeSeparator>:</TimeSeparator>
            <TimeUnit>
              <TimeValue>{countdown.minutes.toString().padStart(2, '0')}</TimeValue>
              <TimeLabel>min</TimeLabel>
            </TimeUnit>
            <TimeSeparator>:</TimeSeparator>
            <TimeUnit>
              <TimeValue>{countdown.seconds.toString().padStart(2, '0')}</TimeValue>
              <TimeLabel>sec</TimeLabel>
            </TimeUnit>
          </CountdownTimer>
          <PredictionTarget>
            📅 {formatDate(nextPrediction.end_date || nextPrediction.target_time || '', 'dd MMM yyyy')}
          </PredictionTarget>
        </CountdownContainer>
      )}
      
      {/* ARCHIVED: User Contest Prediction Card removed (April 2026 contest ended) */}
      {/* userContestPrediction && (<UserContestCard>...</UserContestCard>) */}
      


      {openModal && (
        <MobileSubmitCard onClick={openModal}>
          <SubmitCardContent>
            <SubmitCardIcon>{assetInfo.icon}</SubmitCardIcon>
            <SubmitCardTitle>Submit {assetInfo.name} Prediction</SubmitCardTitle>
            <SubmitCardSubtitle>Share your prediction with the community</SubmitCardSubtitle>
          </SubmitCardContent>
        </MobileSubmitCard>
      )}
      {/* If no active predictions, show first 10 predictions as fallback */}
      {visiblePredictionIds.map((predictionId, index) => {
        const prediction = predictionsMap.get(predictionId); // ⚡ O(1) lookup instead of O(n)
        if (!prediction) return null;
        return (
          <React.Fragment key={prediction.id}>
            <PredictionCard
              ref={(el) => {
                if (el) cardRefs.current.set(prediction.id, el);
              }}
              status={prediction.success_status || 'pending'}
              $active={activePredictions.includes(prediction.id)}
              onClick={() => onPredictionClick(prediction.id)}
              className={`prediction-status-${prediction.success_status || 'pending'}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                <ExpertName>
                  {prediction.prediction_type === 'sentiment' && (
                    <span style={{ marginRight: '6px', fontSize: '14px', color: '#64B5F6' }}>💬</span>
                  )}
                  {prediction.influencer?.name || 'Unknown Expert'}
                </ExpertName>
                {prediction.prediction_type !== 'sentiment' && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: prediction.success_status === 'success' ? 'rgba(39, 174, 96, 0.15)' : 
                                   prediction.success_status === 'miss' ? 'rgba(192, 57, 43, 0.15)' : 
                                   prediction.success_status === 'updated' ? 'rgba(139, 92, 246, 0.15)' :
                                   'rgba(255, 152, 0, 0.15)',
                    color: prediction.success_status === 'success' ? 'rgba(39, 174, 96, 1)' : 
                           prediction.success_status === 'miss' ? 'rgba(192, 57, 43, 1)' : 
                           prediction.success_status === 'updated' ? 'rgba(139, 92, 246, 1)' :
                           'rgba(255, 152, 0, 1)',
                    flexShrink: 0
                  }}>
                    {prediction.success_status === 'success' ? 'SUCCESS' : 
                     prediction.success_status === 'miss' ? 'MISS' : 
                     prediction.success_status === 'updated' ? 'UPDATED' :
                     'PENDING'}
                  </span>
                )}
              </div>
              
              {/* Track Record Section - only show for non-sentiment predictions */}
              {prediction.prediction_type !== 'sentiment' && (
                <ScoreContainer>
                  <ScoreLabel>Track Record:</ScoreLabel>
                  <InfluencerScoreDots 
                    influencerName={prediction.influencer?.name || 'Unknown Expert'} 
                    influencerHistory={influencerHistories.get(prediction.influencer?.name?.trim() || '') || []}
                  />
                </ScoreContainer>
              )}
              {prediction.prediction_made && (
                <div style={{ color: '#6c757d', fontSize: '0.8em', marginBottom: '6px', fontWeight: '400' }}>
                  📅 Made: {formatDate(prediction.prediction_made, 'dd/MM/yyyy')}
                </div>
              )}
              <PredictionText>{formatPrediction(prediction)}</PredictionText>
              <TargetTime>
                <PredictionTypeIcon>
                  {(() => {
                    return prediction.prediction_type === 'target' ? '🎯' : 
                           prediction.prediction_type === 'top' ? '🔝' :
                           prediction.prediction_type === 'bottom' ? '📉' :
                           prediction.prediction_type === 'pricetimerange' ? '⏱️' :
                           prediction.prediction_type === 'bet' ? '🎲' :
                           prediction.prediction_type === 'sentiment' ? '💬' :
                           '📊';
                  })()}
                </PredictionTypeIcon>
                {prediction.prediction_type === 'sentiment' ? (
                  prediction.prediction_made && `📅 Made: ${formatDate(prediction.prediction_made, 'dd/MM/yyyy')}`
                ) : (
                  <>
                    {prediction.prediction_type === 'target' ? 'Target: ' : 
                     prediction.prediction_type === 'top' ? 'Top: ' :
                     prediction.prediction_type === 'bottom' ? 'Bottom: ' :
                     prediction.prediction_type === 'pricetimerange' ? 'Price by: ' :
                     prediction.prediction_type === 'bet' ? (
                       prediction.end_date && new Date(prediction.end_date) < new Date() ? 'Finished: ' : 'Due date: '
                     ) :
                     'Range: '}
                    {prediction.target_time ? formatDate(prediction.target_time, 'dd/MM/yyyy') : prediction.end_date ? formatDate(prediction.end_date, 'dd/MM/yyyy') : 'Date not specified'}
                  </>
                )}
              </TargetTime>

              {prediction.quote && prediction.quote.trim() && (
                <QuoteSection $expanded={true}>
                  <QuoteHeader>
                    <span>💬</span>
                    <span>Original Quote</span>
                  </QuoteHeader>
                  <QuoteText $expanded={true}>
                    {prediction.quote}
                  </QuoteText>
                </QuoteSection>
              )}
              
              <div style={{ marginTop: '8px', borderTop: '1px solid rgba(128, 128, 128, 0.15)', paddingTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <div onClick={(e) => e.stopPropagation()}>
                  <SharePrediction prediction={prediction} subtle />
                </div>
              </div>
            </PredictionCard>

            {shouldShowMobileAdPlaceholder && index === 1 && (
              <PredictionCard status="ad" style={{ cursor: 'default', minHeight: '0' }}>
                <PredictionHeader>
                  <ExpertName>Sponsored</ExpertName>
                  <StatusBadge status="ad">AD</StatusBadge>
                </PredictionHeader>

                <AdPlaceholderBox>
                  <StyledMobileAdSenseSlot
                    slot={BTC_MOBILE_AD_SLOT}
                    format="fluid"
                    layoutKey={BTC_MOBILE_AD_LAYOUT_KEY}
                    fullWidthResponsive={false}
                  />
                </AdPlaceholderBox>
              </PredictionCard>
            )}
          </React.Fragment>
        );
      })}
    </PredictionsList>
  );
};

export default MobilePredictionsList;
